// src/pages/Folder/Installation.tsx

import { Alert, Box, Collapse } from '@mui/material'
import { useEffect, useState } from 'react'

import { FolderId, SensorId, SensorRecord } from '@/types/domain'
import { Severity } from '@/types/mui'

import { installationStatus, SORT } from '../../api'
import { InstallationMenu, usePreloader } from '../../components'
import {
  useFolderInstallations,
  useFolderSensors,
  useSensorPoolIds,
} from '../../hooks'

type InstallationProps = {
  id: FolderId
}

export const Installation = ({ id }: InstallationProps) => {
  const { isLoading } = usePreloader()
  const sIds: SensorId[] = useSensorPoolIds()
  const fSensors: SensorRecord[] = useFolderSensors(id)
  const fInstallations = useFolderInstallations(id)

  const [installations, setInstallations] = useState([])

  useEffect(() => {
    if (!fInstallations || !fSensors || fInstallations.length === 0) return

    fInstallations.forEach(installation => {
      const installationSensors = fSensors
        .filter(f => sIds.includes(Number(f.entity_id)))
        .filter(f => f.installation_id === installation.entity_id)

      installation['sensors'] = installationSensors
    })

    if (fInstallations?.[0]) setInstallations(fInstallations)
  }, [fSensors, fInstallations, id, sIds])

  useEffect(() => {
    if (!fInstallations || !fSensors) {
      setInstallations([])
      return
    }

    if (fInstallations.length === 0) {
      setInstallations([])
      return
    }
  }, [id, fSensors, fInstallations, sIds])

  return (
    <Collapse in={!isLoading}>
      {!isLoading && installations.length === 0 && (
        <Alert severity={'info'} sx={{ mb: 2, border: 'solid 1px #9e9e9e' }}>
          Die Funktion ist nicht verfügbar, da keine Anlage im Gebäude angelegt
          wurde.
        </Alert>
      )}

      <Box
        display={'grid'}
        gap={3}
        gridTemplateColumns={{
          xs: '1fr',
          sm: 'repeat(1, 1fr)',
          md: 'repeat(2, 1fr)',
          lg: 'repeat(2, 1fr)',
          xl: 'repeat(3, 1fr)',
        }}
      >
        {installations.sort(SORT).map(installation => {
          const { sensors, name, entity_id } = installation
          if (sensors.length === 0) return null

          let time = sensors.find(
            f => f.installation_id === installation.entity_id
          )

          time = time.time || ''
          const status = installationStatus(sensors)
          const hours = (Date.now() - new Date(time).getTime()) / 1000 / 60 / 60

          const days = Math.trunc(hours / 24)

          let severity: Severity = 'success'
          if (days >= 3) severity = 'error'
          if (days >= 1 && days < 3) severity = 'warning'
          if (days <= 0) severity = 'success'

          return (
            <InstallationMenu
              id={id}
              installationId={entity_id}
              key={installation.entity_id}
              name={name}
              status={status}
              severity={severity}
              sensors={sensors}
            />
          )
        })}
      </Box>
    </Collapse>
  )
}
